/**
 * @module features/users/components/UserTable
 * @description Adaptive multi-device user directory: full desktop data grid, tablet condensed,
 * and compact stacked mobile cards with view details, edit modal triggers, and delete confirmation.
 */

import * as React from "react";
import {
  Trash2,
  Edit,
  KeyRound,
  Fingerprint,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Loader2,
  Eye,
  ShieldOff,
} from "lucide-react";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { UserAvatar } from "./UserAvatar";
import { UserDto } from "@/types/auth";
import { PaginationMeta } from "@/types/users";
import { useDeleteUser } from "../api/users-api";
import { AppError } from "@/lib/api-client";

interface UserTableProps {
  users: UserDto[];
  pagination?: PaginationMeta;
  onPageChange: (newPage: number) => void;
  onEditUser?: (user: UserDto) => void;
  onViewUser?: (user: UserDto) => void;
  onDisableTotp?: (user: UserDto) => void;
}

export function UserTable({
  users,
  pagination,
  onPageChange,
  onEditUser,
  onViewUser,
  onDisableTotp,
}: UserTableProps) {
  const [deleteTarget, setDeleteTarget] = React.useState<UserDto | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const deleteMutation = useDeleteUser();

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);

    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err: any) {
      if (err instanceof AppError) {
        setDeleteError(err.errorDetails);
      } else {
        setDeleteError("Failed to delete user. Please try again.");
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* ----------------------------------------------------------------- */}
      {/* 1. Desktop & Tablet Table View (>=640px) */}
      {/* ----------------------------------------------------------------- */}
      <div className="hidden sm:block rounded-xl border border-border/80 bg-card overflow-hidden shadow-xs">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30 border-b border-border/80">
              <TableHead className="w-[280px] text-xs font-medium">User</TableHead>
              <TableHead className="text-xs font-medium">Role</TableHead>
              <TableHead className="hidden md:table-cell text-xs font-medium">Phone</TableHead>
              <TableHead className="hidden lg:table-cell text-xs font-medium">Security</TableHead>
              <TableHead className="text-xs font-medium">Status</TableHead>
              <TableHead className="text-right text-xs font-medium">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="h-32 text-center text-muted-foreground text-xs"
                >
                  No users matched your query.
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.id}
                  onClick={() => onViewUser?.(user)}
                  className="h-[52px] hover:bg-muted/20 cursor-pointer transition-colors border-b border-border/60"
                >
                  {/* User Profile Cell */}
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        name={user.name}
                        avatarUrl={user.avatar_url}
                        size="md"
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-foreground truncate">
                          {user.name}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono truncate">
                          {user.email_id}
                        </p>
                      </div>
                    </div>
                  </TableCell>

                  {/* Role Badge with semantic color */}
                  <TableCell>
                    <span
                      className={`inline-flex items-center text-[11px] font-mono font-medium px-2 py-0.5 rounded-full border ${
                        user.role === "super_admin"
                          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                          : "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20"
                      }`}
                    >
                      {user.role === "super_admin" ? "Super Admin" : "Client"}
                    </span>
                  </TableCell>

                  {/* Phone */}
                  <TableCell className="hidden md:table-cell text-xs font-mono text-muted-foreground">
                    {user.contact_number
                      ? `${user.country_code} ${user.contact_number}`
                      : "—"}
                  </TableCell>

                  {/* Security (2FA & Passkey) */}
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex items-center gap-1.5">
                      {user.is_totp_enabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDisableTotp?.(user);
                          }}
                          className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-colors"
                          title="2FA Enabled - Click to Disable"
                        >
                          <KeyRound className="h-3 w-3" />
                          2FA
                        </button>
                      )}
                      {user.passkey_count > 0 && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-mono font-medium px-1.5 py-0.5 rounded bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-xs"
                          title={`${user.passkey_count} of ${user.max_passkeys || 5} Passkeys registered`}
                        >
                          <Fingerprint className="h-3 w-3" />
                          {user.passkey_count}/{user.max_passkeys || 5}
                        </span>
                      )}
                      {!user.is_totp_enabled && user.passkey_count === 0 && (
                        <span className="text-xs text-muted-foreground/70 font-mono">
                          Password
                        </span>
                      )}
                    </div>
                  </TableCell>

                  {/* Session Status */}
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`h-2 w-2 rounded-full ${
                          user.is_logged_in ? "bg-emerald-500" : "bg-muted-foreground/30"
                        }`}
                      />
                      <span className="text-xs text-muted-foreground font-medium">
                        {user.is_logged_in ? "Online" : "Offline"}
                      </span>
                    </div>
                  </TableCell>

                  {/* Actions (stop propagation so row click opens detail) */}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {user.is_totp_enabled && onDisableTotp && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDisableTotp(user)}
                          aria-label={`Disable 2FA for ${user.name}`}
                          className="h-8 w-8 text-purple-600 dark:text-purple-400 hover:text-destructive hover:bg-destructive/10"
                          title="Disable 2FA for this user"
                        >
                          <ShieldOff className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {onViewUser && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onViewUser(user)}
                          aria-label={`View ${user.name}`}
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          title="View user details"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {onEditUser && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEditUser(user)}
                          aria-label={`Edit ${user.name}`}
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          title="Edit user"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteTarget(user);
                        }}
                        aria-label={`Delete ${user.name}`}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        title="Delete user"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* 2. Mobile Compact Cards View (<640px) */}
      {/* ----------------------------------------------------------------- */}
      <div className="sm:hidden space-y-2.5">
        {users.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-6 text-center text-xs text-muted-foreground">
            No users matched your query.
          </div>
        ) : (
          users.map((user) => (
            <div
              key={user.id}
              onClick={() => onViewUser?.(user)}
              className="rounded-xl border border-border/80 bg-card p-3 shadow-xs space-y-2.5 cursor-pointer active:scale-[0.99] transition-transform"
            >
              <div className="flex items-start justify-between gap-2.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <UserAvatar
                    name={user.name}
                    avatarUrl={user.avatar_url}
                    size="sm"
                  />
                  <div className="min-w-0">
                    <h3 className="font-medium text-xs text-foreground truncate">
                      {user.name}
                    </h3>
                    <p className="text-[11px] text-muted-foreground font-mono truncate">
                      {user.email_id}
                    </p>
                  </div>
                </div>

                <span
                  className={`inline-flex items-center text-[10px] font-mono font-medium px-2 py-0.5 rounded-full border shrink-0 ${
                    user.role === "super_admin"
                      ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 border-zinc-900 dark:border-zinc-200"
                      : "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20"
                  }`}
                >
                  {user.role === "super_admin" ? "Admin" : "Client"}
                </span>
              </div>

              <div
                className="flex items-center justify-between pt-1 border-t border-border/60 text-[11px] text-muted-foreground"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      user.is_logged_in ? "bg-emerald-500" : "bg-muted-foreground/30"
                    }`}
                  />
                  <span>{user.is_logged_in ? "Online" : "Offline"}</span>
                  {user.passkey_count > 0 && (
                    <span className="flex items-center gap-0.5 text-foreground font-mono font-medium">
                      <Fingerprint className="h-3 w-3" />
                      {user.passkey_count}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {user.is_totp_enabled && onDisableTotp && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDisableTotp(user)}
                      className="h-7 px-2 text-xs text-purple-600 dark:text-purple-400 hover:text-destructive hover:bg-destructive/10"
                      title="Disable 2FA for this user"
                    >
                      <ShieldOff className="h-3.5 w-3.5 mr-1" />
                      Disable 2FA
                    </Button>
                  )}
                  {onEditUser && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditUser(user)}
                      className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <Edit className="h-3.5 w-3.5 mr-1" />
                      Edit
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteTarget(user);
                    }}
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
          <p className="font-mono">
            Showing {(pagination.page - 1) * pagination.page_size + 1}–
            {Math.min(pagination.page * pagination.page_size, pagination.total || pagination.total_count)}{" "}
            of {pagination.total || pagination.total_count}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange(pagination.page - 1)}
              className="h-8 gap-1 text-xs"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              <span>Previous</span>
            </Button>
            <span className="font-mono px-1">
              {pagination.page} / {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => onPageChange(pagination.page + 1)}
              className="h-8 gap-1 text-xs"
            >
              <span>Next</span>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(isOpen) => !isOpen && setDeleteTarget(null)}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10 text-destructive mb-2">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <DialogTitle className="text-center text-base">
              Delete User Account
            </DialogTitle>
            <DialogDescription className="text-center text-xs">
              Are you sure you want to permanently delete{" "}
              <strong className="text-foreground">{deleteTarget?.name}</strong> (
              {deleteTarget?.email_id})? All associated passkeys and session tokens will be immediately revoked.
            </DialogDescription>
          </DialogHeader>

          {deleteError && (
            <div className="p-3 rounded-lg bg-danger-soft text-danger text-xs border border-danger/20">
              {deleteError}
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-between pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="min-w-[100px]"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Account"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
